/* ==========================================================================
   <bundle-offers> custom element
   - Selects a bundle offer and syncs its quantity into the product form
   - Re-prices every offer when the variant changes
   - Supports the Theme Editor (block select, section reload)
   ========================================================================== */

if (!customElements.get('bundle-offers')) {
  class BundleOffers extends HTMLElement {
    constructor() {
      super();
      this.tiers = [];
      this.variants = [];
      this.form = null;
      this.pollId = null;
      this.observer = null;
      this.currentVariantId = null;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onVariantEvent = this.onVariantEvent.bind(this);
      this.onFormChange = this.onFormChange.bind(this);
    }

    connectedCallback() {
      this.classList.add('bo--js');
      this.tiers = Array.from(this.querySelectorAll('[data-bo-tier]'));
      if (this.tiers.length === 0) return;

      this.errorEl = this.querySelector('[data-bo-error]');
      this.addButton = this.querySelector('[data-bo-add]');
      this.moneyFormat = this.dataset.moneyFormat || '$' + '{' + '{amount}' + '}';
      this.trimZeros = this.dataset.trimZeros === 'true';
      this.variants = this.readVariants();
      this.form = this.findForm();
      this.currentVariantId = this.readVariantId();

      this.bindTiers();
      this.bindButton();
      this.watchVariant();
      this.revealTiers();

      document.addEventListener('shopify:block:select', this.onBlockSelect);

      const initial =
        this.tiers.find((tier) => tier.dataset.selected === 'true') ||
        this.tiers.find((tier) => tier.querySelector('input').checked) ||
        this.tiers[0];

      this.renderPrices();
      this.select(initial, false);
    }

    disconnectedCallback() {
      if (this.pollId) clearInterval(this.pollId);
      if (this.observer) this.observer.disconnect();
      if (this.form) this.form.removeEventListener('change', this.onFormChange);
      document.removeEventListener('variant:change', this.onVariantEvent);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* Data ------------------------------------------------------------------ */

    readVariants() {
      const node = this.querySelector('[data-bo-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    findForm() {
      const section = this.closest('.shopify-section') || document;
      const scoped = Array.from(section.querySelectorAll('form[action*="/cart/add"]'));
      const forms = scoped.length ? scoped : Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
      if (forms.length === 0) return null;

      const ids = this.variants.map((variant) => String(variant.id));
      const match = forms.find((form) => {
        const input = form.querySelector('[name="id"]');
        return input && ids.indexOf(String(input.value)) !== -1;
      });

      return match || forms[0];
    }

    readVariantId() {
      if (!this.form) return this.variants.length ? this.variants[0].id : null;
      const input = this.form.querySelector('[name="id"]');
      return input ? Number(input.value) : null;
    }

    basePrice() {
      const variant = this.variants.find((item) => item.id === this.currentVariantId);
      if (variant) return variant.price;
      return this.variants.length ? this.variants[0].price : 0;
    }

    /* Money ----------------------------------------------------------------- */

    formatMoney(cents) {
      const value = typeof cents === 'string' ? Number(cents.replace(/[^0-9.-]/g, '')) : cents;
      if (isNaN(value)) return '';

      const format = this.moneyFormat;
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      const type = match ? match[1] : 'amount';

      const withDelimiters = (number, precision, thousands, decimal) => {
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const rest = parts[1] ? decimal + parts[1] : '';
        return whole + rest;
      };

      let formatted;

      switch (type) {
        case 'amount_no_decimals':
          formatted = withDelimiters(value, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          formatted = withDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = withDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = withDelimiters(value, 2, "'", '.');
          break;
        case 'amount_with_space_separator':
          formatted = withDelimiters(value, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          formatted = withDelimiters(value, 0, ' ', ',');
          break;
        default:
          formatted = withDelimiters(value, 2, ',', '.');
      }

      if (this.trimZeros) formatted = formatted.replace(/[.,]00$/, '');

      return format.replace(placeholder, formatted).replace(/<[^>]*>/g, '');
    }

    /* Pricing --------------------------------------------------------------- */

    tierPrices(tier) {
      const base = this.basePrice();
      const quantity = Math.max(parseInt(tier.dataset.quantity, 10) || 1, 1);
      const customRaw = (tier.dataset.customPrice || '').trim();
      const custom = parseFloat(customRaw);
      const discount = Math.min(Math.max(parseInt(tier.dataset.discount, 10) || 0, 0), 99);

      const unit = customRaw !== '' && !isNaN(custom) ? Math.round(custom * 100) : Math.floor((base * (100 - discount)) / 100);

      const total = unit * quantity;
      const compare = base * quantity;
      const saved = Math.max(compare - total, 0);
      const percent = compare > 0 ? Math.floor((saved * 100) / compare) : 0;

      return { quantity, unit, total, compare, saved, percent };
    }

    fillTemplate(template, price) {
      return String(template || '')
        .split('[unit_price]').join(this.formatMoney(price.unit))
        .split('[total]').join(this.formatMoney(price.total))
        .split('[compare]').join(this.formatMoney(price.compare))
        .split('[saved]').join(this.formatMoney(price.saved))
        .split('[percent]').join(String(price.percent))
        .split('[quantity]').join(String(price.quantity));
    }

    renderPrices() {
      this.tiers.forEach((tier) => {
        const price = this.tierPrices(tier);

        const totalEl = tier.querySelector('[data-bo-total]');
        const compareEl = tier.querySelector('[data-bo-compare]');

        if (totalEl) totalEl.textContent = this.formatMoney(price.total);
        if (compareEl) {
          compareEl.textContent = this.formatMoney(price.compare);
          compareEl.hidden = price.saved <= 0;
        }

        tier.querySelectorAll('[data-template]').forEach((node) => {
          node.textContent = this.fillTemplate(node.dataset.template, price);
        });
      });

      this.renderSummary();
    }

    renderSummary() {
      const tier = this.selectedTier();
      if (!tier) return;

      const price = this.tierPrices(tier);
      const totalEl = this.querySelector('[data-bo-summary-total]');
      const compareEl = this.querySelector('[data-bo-summary-compare]');
      const savedEl = this.querySelector('[data-bo-summary-saved]');

      if (totalEl) totalEl.textContent = this.formatMoney(price.total);
      if (compareEl) compareEl.textContent = price.saved > 0 ? this.formatMoney(price.compare) : '';
      if (savedEl) {
        savedEl.textContent = price.saved > 0 ? this.formatMoney(price.saved) + ' ' + (this.dataset.savedLabel || '') : '';
      }
    }

    /* Selection ------------------------------------------------------------- */

    selectedTier() {
      return this.tiers.find((tier) => tier.dataset.selected === 'true') || null;
    }

    bindTiers() {
      this.tiers.forEach((tier) => {
        const input = tier.querySelector('input');
        if (input) input.addEventListener('change', () => this.select(tier, true));
      });
    }

    select(tier, userAction) {
      if (!tier) return;

      this.tiers.forEach((item) => {
        const isTarget = item === tier;
        item.dataset.selected = isTarget ? 'true' : 'false';
        item.classList.toggle('is-selected', isTarget);
        const input = item.querySelector('input');
        if (input) input.checked = isTarget;
      });

      if (userAction && this.classList.contains('bo--pulse')) {
        tier.classList.remove('is-animating');
        void tier.offsetWidth;
        tier.classList.add('is-animating');
        tier.addEventListener('animationend', () => tier.classList.remove('is-animating'), { once: true });
      }

      const quantity = this.tierPrices(tier).quantity;
      this.applyQuantity(quantity);
      this.renderSummary();
      this.clearError();

      if (userAction) {
        this.dispatchEvent(new CustomEvent('bundle-offers:change', { bubbles: true, detail: { quantity } }));
      }
    }

    applyQuantity(quantity) {
      if (this.dataset.syncQuantity !== 'true' || !this.form) return;

      let input = this.form.querySelector('[name="quantity"]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.setAttribute('data-bo-quantity', '');
        this.form.appendChild(input);
      }

      if (String(input.value) === String(quantity)) return;

      input.value = quantity;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Theme Editor ---------------------------------------------------------- */

    onBlockSelect(event) {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      const tier = this.tiers.find((item) => item.dataset.blockId === blockId);
      if (tier) this.select(tier, false);
    }

    /* Variants -------------------------------------------------------------- */

    checkVariant() {
      const id = this.readVariantId();
      if (!id || id === this.currentVariantId) return;
      this.currentVariantId = id;
      this.renderPrices();
      const tier = this.selectedTier();
      if (tier) this.applyQuantity(this.tierPrices(tier).quantity);
    }

    onFormChange() {
      window.requestAnimationFrame(() => this.checkVariant());
    }

    onVariantEvent() {
      window.requestAnimationFrame(() => this.checkVariant());
    }

    watchVariant() {
      if (!this.form) return;
      this.form.addEventListener('change', this.onFormChange);
      document.addEventListener('variant:change', this.onVariantEvent);
      this.pollId = setInterval(() => {
        if (!document.hidden) this.checkVariant();
      }, 700);
    }

    /* Add to cart ----------------------------------------------------------- */

    bindButton() {
      if (!this.addButton) return;
      this.addButton.addEventListener('click', () => this.addToCart());
    }

    addToCart() {
      const tier = this.selectedTier();
      if (!tier || !this.form) return;

      const variantId = this.readVariantId();
      if (!variantId) {
        this.showError(this.dataset.errorLabel);
        return;
      }

      const price = this.tierPrices(tier);
      const original = this.addButton.textContent;
      this.addButton.disabled = true;
      this.addButton.textContent = this.dataset.addingLabel || original;
      this.clearError();

      const body = new FormData(this.form);
      body.set('id', variantId);
      body.set('quantity', price.quantity);
      body.delete('sections');
      body.delete('sections_url');

      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status && data.status !== 200) {
            this.showError(data.description || data.message || this.dataset.errorLabel);
            return;
          }

          const code = tier.dataset.discountCode;
          const after = this.dataset.afterAdd;

          if (code) {
            const target = after === 'checkout' ? '/checkout' : '/cart';
            window.location.href = root + 'discount/' + encodeURIComponent(code) + '?redirect=' + target;
            return;
          }

          if (after === 'cart') {
            window.location.href = root + 'cart';
            return;
          }

          if (after === 'checkout') {
            window.location.href = root + 'checkout';
            return;
          }

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        })
        .catch(() => this.showError(this.dataset.errorLabel))
        .finally(() => {
          this.addButton.disabled = false;
          this.addButton.textContent = original;
        });
    }

    showError(message) {
      if (this.errorEl) this.errorEl.textContent = message || '';
    }

    clearError() {
      if (this.errorEl) this.errorEl.textContent = '';
    }

    /* Reveal ---------------------------------------------------------------- */

    revealTiers() {
      if (!this.classList.contains('bo--reveal')) return;

      const designMode = window.Shopify && window.Shopify.designMode;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (designMode || reduced || typeof IntersectionObserver === 'undefined') {
        this.tiers.forEach((tier) => tier.classList.add('is-visible'));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = this.tiers.indexOf(entry.target);
            entry.target.style.transitionDelay = Math.max(index, 0) * 70 + 'ms';
            entry.target.classList.add('is-visible');
            entry.target.addEventListener(
              'transitionend',
              () => {
                entry.target.style.transitionDelay = '';
              },
              { once: true }
            );
            this.observer.unobserve(entry.target);
          });
        },
        { threshold: 0.1 }
      );

      this.tiers.forEach((tier) => this.observer.observe(tier));
    }
  }

  if (!window.customElements.get('bundle-offers')) {
    window.customElements.define('bundle-offers', BundleOffers);
  }
}
